import { useEffect, useRef, useState } from "react";

const DEVELOPMENT_SITE_KEY = "1x00000000000000000000AA";

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

export function executeTurnstileChallenge(turnstile: Pick<TurnstileApi, "reset" | "execute"> | undefined, widgetId: string | null, onClearToken: () => void) {
  if (!turnstile || !widgetId) return false;
  onClearToken();
  turnstile.reset(widgetId);
  turnstile.execute(widgetId);
  return true;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<TurnstileApi> | null = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loader) return loader;

  loader = new Promise<TurnstileApi>((resolve, reject) => {
    const finish = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not initialize."));
    const existing = document.querySelector<HTMLScriptElement>('script[data-partition-stalls-turnstile="true"]');
    if (existing) {
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", () => reject(new Error("Turnstile could not be loaded.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.partitionStallsTurnstile = "true";
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile could not be loaded.")), { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    loader = null;
    throw error;
  });

  return loader;
}

type TurnstileWidgetProps = {
  onVerify: (token: string) => void;
  onError: (message: string) => void;
  resetSignal: number;
  executeSignal: number;
};

export function TurnstileWidget({ onVerify, onError, resetSignal, executeSignal }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const pendingExecuteRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const siteKey = import.meta.env.DEV ? DEVELOPMENT_SITE_KEY : (import.meta.env.VITE_TURNSTILE_SITE_KEY || "");

  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  const refreshChallenge = () => {
    onVerifyRef.current("");
    if (widgetRef.current && window.turnstile) {
      window.turnstile.reset(widgetRef.current);
      window.turnstile.execute(widgetRef.current);
    }
  };

  const executePendingChallenge = () => {
    return executeTurnstileChallenge(window.turnstile, widgetRef.current, () => onVerifyRef.current(""));
  };

  useEffect(() => {
    if (!siteKey) {
      setLoading(false);
      onErrorRef.current("Human verification is temporarily unavailable. Please call 646-349-4040.");
      return;
    }

    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "quote_submit",
          theme: "light",
          size: "compact",
          execution: "execute",
          retry: "auto",
          "refresh-expired": "auto",
          "refresh-timeout": "auto",
          callback: (token: string) => onVerifyRef.current(token),
          "expired-callback": () => {
            refreshChallenge();
            onErrorRef.current("Human verification expired. A fresh check is ready below.");
          },
          "timeout-callback": () => {
            refreshChallenge();
            onErrorRef.current("Human verification timed out. A fresh check is ready below.");
          },
          "error-callback": () => {
            onVerifyRef.current("");
            onErrorRef.current("Human verification could not be completed. Please retry.");
          },
        });
        if (pendingExecuteRef.current && executePendingChallenge()) pendingExecuteRef.current = false;
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        onErrorRef.current(error instanceof Error ? error.message : "Human verification could not be loaded.");
      });

    return () => {
      cancelled = true;
      pendingExecuteRef.current = false;
      if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetSignal > 0 && widgetRef.current && window.turnstile) {
      pendingExecuteRef.current = false;
      window.turnstile.reset(widgetRef.current);
    }
  }, [resetSignal]);

  useEffect(() => {
    if (executeSignal <= 0) return;
    pendingExecuteRef.current = true;
    if (executePendingChallenge()) pendingExecuteRef.current = false;
  }, [executeSignal]);

  return (
    <div className="turnstile-widget" aria-busy={loading}>
      {loading && <p>Loading human verification…</p>}
      <div ref={containerRef} />
    </div>
  );
}
